import { useParams } from 'react-router'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import {
  ArrowsInSimpleIcon,
  ArrowsOutSimpleIcon,
  EmptyState,
  FlagIcon,
  HashIcon,
  Mtds,
  PencilSimpleIcon,
  ScreenHeader,
  Timeline,
  useGetSampleHistoryQuery
} from 'prove-common'
import { type HistoryTimelineItem } from '../../types/types'
import { AppLayout } from '../../components/app-layout/AppLayout'
import {
  hasExpandableHistoryDetails,
  HistoryTimelineCard
} from '../../components/history-timeline-card/HistoryTimelineCard'
import { toLocaleDateString } from '../../utils/dateUtils'
import { presentHistoryItems } from '../../utils/historyPresentation'

type SampleChangelogScreenProps = {
  topBar?: ReactNode
  sidebar?: ReactNode
  useMobileBar?: boolean
}

export const SampleChangelogScreen = ({
  topBar,
  sidebar,
  useMobileBar
}: SampleChangelogScreenProps) => {
  const { sampleId } = useParams()
  const {
    data: history,
    isPending,
    isError
  } = useGetSampleHistoryQuery(sampleId)
  const [openHistoryItemIds, setOpenHistoryItemIds] = useState<string[]>([])
  const historyEndRef = useRef<HTMLDivElement>(null)
  const historyItems = presentHistoryItems(history?.items ?? [])
  const expandableHistoryItems = historyItems.filter(
    hasExpandableHistoryDetails
  )
  const historyGroups = groupHistoryItemsByDate(historyItems)

  useLayoutEffect(() => {
    if (isPending || historyItems.length === 0) return

    const animationFrame = window.requestAnimationFrame(() => {
      historyEndRef.current?.scrollIntoView({ block: 'end' })
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [historyItems.length, isPending, sampleId])

  const handleEdit = (item: HistoryTimelineItem): void => {
    setOpenHistoryItemIds((prevOpenItemIds) =>
      prevOpenItemIds.includes(item.id)
        ? prevOpenItemIds.filter((openItemId) => openItemId !== item.id)
        : [...prevOpenItemIds, item.id]
    )
  }

  const addAllHistoryItemsToOpen = () => {
    setOpenHistoryItemIds(expandableHistoryItems.map((item) => item.id))
  }

  const removeAllHistoryItemsToOpen = () => {
    setOpenHistoryItemIds([])
  }

  return (
    <AppLayout
      sidebar={sidebar}
      useMobileBar={useMobileBar}
      isLoading={isPending}
      isError={isError}
      topBar={topBar}
    >
      <ScreenHeader
        title={
          <Mtds.Flex data-align='center' data-gap='1'>
            <HashIcon />
            {sampleId}
          </Mtds.Flex>
        }
        actions={
          <Mtds.Flex data-gap='2'>
            {expandableHistoryItems.length > 1 && (
              <Mtds.Button
                data-variant='secondary'
                onClick={
                  openHistoryItemIds.length === 0
                    ? addAllHistoryItemsToOpen
                    : removeAllHistoryItemsToOpen
                }
              >
                {openHistoryItemIds.length === 0 ? (
                  <>
                    Utvid alle
                    <ArrowsOutSimpleIcon />
                  </>
                ) : (
                  <>
                    Lukk alle
                    <ArrowsInSimpleIcon />
                  </>
                )}
              </Mtds.Button>
            )}
          </Mtds.Flex>
        }
      />
      {historyItems.length === 0 ? (
        <div>
          <EmptyState
            title='Ingen endringer registrert'
            description='Denne prøven har ingen endringslogg ennå.'
          />
        </div>
      ) : (
        <Mtds.Grid data-gap={5}>
          {historyGroups.map((group, groupIndex) => (
            <Mtds.Grid key={group.date} data-gap={3}>
              <Mtds.Heading as='h2' data-size='sm'>
                {group.date}
              </Mtds.Heading>
              <Mtds.Flex data-gap={1} className='flex-col'>
                {group.items.map((item, itemIndex) => {
                  const isLastItem =
                    groupIndex === historyGroups.length - 1 &&
                    itemIndex === group.items.length - 1
                  return (
                    <div key={item.id} data-color={historyItemColor(item)}>
                      <div className='flex gap-2 items-start'>
                        <Timeline
                          showLine={!isLastItem}
                          icon={historyItemIcon(item)}
                          label={historyItemLabel(item)}
                        />
                        <HistoryTimelineCard
                          item={item}
                          isOpen={openHistoryItemIds.includes(item.id)}
                          onToggle={handleEdit}
                        />
                      </div>
                    </div>
                  )
                })}
              </Mtds.Flex>
            </Mtds.Grid>
          ))}
          <div ref={historyEndRef} />
        </Mtds.Grid>
      )}
    </AppLayout>
  )
}

const groupHistoryItemsByDate = (items: HistoryTimelineItem[]) => {
  return items
    .reduce<{ date: string; items: HistoryTimelineItem[] }[]>(
      (groups, item) => {
        const date = toLocaleDateString(item.occurredAt) ?? ''
        const currentGroup = groups[groups.length - 1]
        if (currentGroup?.date === date) {
          currentGroup.items.push(item)
        } else {
          groups.push({ date, items: [item] })
        }
        return groups
      },
      []
    )
    .reverse()
    .map((group) => ({ ...group, items: group.items.toReversed() }))
}

const historyItemColor = (item: HistoryTimelineItem) => {
  return item.source === 'audit' ? 'info' : 'warning'
}

const historyItemLabel = (item: HistoryTimelineItem) => {
  return item.source === 'audit' ? 'Endringsregistrering' : 'Hendelse'
}

const historyItemIcon = (item: HistoryTimelineItem) => {
  return item.source === 'audit' ? <PencilSimpleIcon /> : <FlagIcon />
}
